import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {Alert, Card, Spinner} from 'react-bootstrap';
import TabComponent, {TabData} from '../../../app/common/components/TabComponent';
import Level3Title from '../../../app/common/components/title/Level3Title';
import ArpPage from '../Components/ArpPage';
import HomePage from '../Components/Home/HomePage';
import LanPage from '../Components/Lan/LanPage';
import FirewallPrivePage from '../Components/Firewall/FirewallPrivePage';
import FirewallPublicPage from '../Components/Firewall/FirewallPublicPage';
import WlanPage from '../Components/Wlan/WlanPage';
import VpnL2tpPage from '../Components/Vpn/VpnL2tpPage';
import PingPage from '../Components/Ping/PingPage';
import TraceroutePage from '../Components/Traceroute/TraceroutePage';
import {getServerMessage, useGetRouterContext} from '../Hooks/RouterManager';
import NotFoundRedirect from '../../../app/common/components/NotFoundRedirect';
import {isNotFoundError} from '../../../app/common/tools/httpError';

const RouterConfigurationPage = (): JSX.Element => {
  const intl = useIntl();
  const {routerId = ''} = useParams<{routerId: string}>();
  const [activeTab, setActiveTab] = useState('home');
  const {data: context, isLoading, isError, error} = useGetRouterContext(routerId);

  if (isNotFoundError(error)) {
    return <NotFoundRedirect />;
  }

  if (isError) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            {getServerMessage(error) ?? intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ERROR'})}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (isLoading || !context) {
    return (
      <Card>
        <Card.Body className="text-center py-10">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  // Onglets conditionnels — porté de leftside.php (v1) :
  // LAN masqué pour les services VPN, WiFi selon capacité, outils réseau en RouterOS > 4.
  const tabData: TabData[] = [
    {
      title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.HOME'}),
      key: 'home',
      content: <HomePage />,
    },
    ...(!context.hasVpn
      ? [
          {
            title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN'}),
            key: 'lan',
            content: <LanPage routerId={routerId} />,
          },
        ]
      : []),
    {
      title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE'}),
      key: 'firewall-prive',
      content: <FirewallPrivePage routerId={routerId} />,
    },
    {
      title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC'}),
      key: 'firewall-public',
      content: <FirewallPublicPage routerId={routerId} />,
    },
    ...(context.hasWifi
      ? [
          {
            title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN'}),
            key: 'wlan',
            content: <WlanPage routerId={routerId} band="2.4GHz" />,
          },
          {
            title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN6'}),
            key: 'wlan6',
            content: <WlanPage routerId={routerId} band="5GHz" />,
          },
        ]
      : []),
    {
      title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.L2TP'}),
      key: 'vpn-l2tp',
      content: <VpnL2tpPage routerId={routerId} />,
    },
    {
      title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.ARP'}),
      key: 'arp',
      content: <ArpPage routerId={routerId} />,
    },
    ...(context.supportsTools
      ? [
          {
            title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING'}),
            key: 'ping',
            content: <PingPage routerId={routerId} />,
          },
          {
            title: intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE'}),
            key: 'traceroute',
            content: <TraceroutePage routerId={routerId} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <Level3Title title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TITRE'})} />
          <div className="d-flex flex-wrap gap-4 mt-3 text-muted">
            <div>
              <strong>IP : </strong>
              {context.ip}
            </div>
            {context.identity && (
              <div>
                <strong>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.IDENTITY'})} : </strong>
                {context.identity}
              </div>
            )}
            {context.model && (
              <div>
                <strong>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.MODEL'})} : </strong>
                {context.model}
              </div>
            )}
            {context.firmware && (
              <div>
                <strong>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIRMWARE'})} : </strong>
                {context.firmware}
              </div>
            )}
            <div>
              <span className={`badge bg-${context.connected ? 'success' : 'danger'}`}>
                {intl.formatMessage({
                  id: context.connected
                    ? 'CONFIGURATION.ROUTEUR.CONNECTED'
                    : 'CONFIGURATION.ROUTEUR.DISCONNECTED',
                })}
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      <TabComponent tabData={tabData} activeKey={activeTab} onTabChange={setActiveTab} />
    </>
  );
};

export default RouterConfigurationPage;
